import { computed, ref } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";

const currentActiveObject = computed<fabric.Object>(() => store.getters.currentActiveObject);

export const iconAction = ref("");
export const selectedObjectId = ref(0);

export const lockIcon = ref({} as Icon);
export const unlockIcon = ref({} as Icon);
export const deleteIcon = ref({} as Icon);
export const arrangeIcon = ref({} as Icon);

const iconsHelperLine = ref(
  new fabric.Line(undefined, {
    originX: "center",
    originY: "center",
    selectable: false,
    evented: false,
    dirty: true,
    opacity: 0,
    stroke: "#ff0000",
    strokeWidth: 1,
    name: "iconsHelperLine",
  })
);

abstract class Icon {
  icon: fabric.Object;
  name: string;
  padding: number;
  position: string;

  constructor(name: string, position: string) {
    this.icon = {} as fabric.Object;
    this.name = name;
    this.padding = 2.5;
    this.position = position;
  }

  public abstract utility(): void;

  public loadIcon(): Promise<fabric.Object> {
    const upperCaseName = `${this.name.slice(0, 1).toUpperCase()}${this.name.slice(
      1,
      this.name.length
    )}`;

    return new Promise((resolve) => {
      fabric.loadSVGFromURL(require(`@/assets/icons/${this.name}.svg`), function(results, options) {
        const icon = fabric.util.groupSVGElements(results, options) as fabric.Object;
        

        icon.set({
          hasControls: false,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          originX: "center",
          originY: "center",
          name: `objectIcon${upperCaseName}`,
          scaleX: 0.75,
          scaleY: 0.75,
          opacity: 0,
          dirty: true,
        });

        resolve(icon);
      });
    });
  }

  public getLeft() {
    return this.icon.left as number;
  }

  public getTop() {
    return this.icon.top as number;
  }

  public getWidth() {
    const width = this.icon.width as number;
    const scale = this.icon.scaleX as number;
    return width * scale;
  }

  public getHeight() {
    const height = this.icon.height as number;
    const scale = this.icon.scaleY as number;
    return height * scale;
  }

  public hide() {
    this.icon.set({
      opacity: 0,
      hoverCursor: "default",
      selectable: false,
      evented: false,
    });
  }

  public show(activeObject: fabric.Object) {
    selectedObjectId.value = activeObject.id as number;

    switch (this.position) {
      case "left": {
        const tlX = iconsHelperLine.value.aCoords?.tl.x as number;
        const tlY = iconsHelperLine.value.aCoords?.tl.y as number;
        const blX = iconsHelperLine.value.aCoords?.bl.x as number;
        const blY = iconsHelperLine.value.aCoords?.bl.y as number;

        this.icon
          .set({
            left: (tlX + blX) / 2,
            top: (tlY + blY) / 2,
            opacity: 1,
            hoverCursor: "pointer",
            selectable: true,
            evented: true,
          })
          .setCoords();
        break;
      }
      case "center": {
        const tlX = iconsHelperLine.value.aCoords?.tl.x as number;
        const tlY = iconsHelperLine.value.aCoords?.tl.y as number;
        const trX = iconsHelperLine.value.aCoords?.tr.x as number;
        const brY = iconsHelperLine.value.aCoords?.br.y as number;

        this.icon
          .set({
            left: (tlX + trX) / 2,
            top: (tlY + brY) / 2,
            opacity: 1,
            hoverCursor: "pointer",
            selectable: true,
            evented: true,
          })
          .setCoords();
        break;
      }
      case "right": {
        const trX = iconsHelperLine.value.aCoords?.tr.x as number;
        const trY = iconsHelperLine.value.aCoords?.tr.y as number;
        const brX = iconsHelperLine.value.aCoords?.br.x as number;
        const brY = iconsHelperLine.value.aCoords?.br.y as number;

        this.icon
          .set({
            left: (trX + brX) / 2,
            top: (trY + brY) / 2,
            opacity: 1,
            hoverCursor: "pointer",
            selectable: true,
            evented: true,
          })
          .setCoords();
        break;
      }
    }
  }
}

class LockIcon extends Icon {
  constructor(name: string, position: string) {
    super(name, position);
  }

  static async create(name: string, position: string) {
    const i = new LockIcon(name, position);
    i.icon = await i.loadIcon();
    return i;
  }

  utility(): void {
    this.icon.on("mousedown", () => {
      iconAction.value = "lock";
    });

    this.icon.on("mouseover", () => {
      iconAction.value = "show_lock";
    });

    this.icon.on("mouseout", () => {
      iconAction.value = "hide_icon";
    })
  }
}

class DeleteIcon extends Icon {
  constructor(name: string, position: string) {
    super(name, position);
  }

  static async create(name: string, position: string) {
    const i = new DeleteIcon(name, position);
    i.icon = await i.loadIcon();
    return i;
  }

  utility(): void {
    this.icon.on("mousedown", () => {
      iconAction.value = "delete";
    });

    this.icon.on("mouseover", () => {
      iconAction.value = "show_delete";
    });

    this.icon.on("mouseout", () => {
      iconAction.value = "hide_icon";
    })
  }
}

class ArrangeIcon extends Icon {
  constructor(name: string, position: string) {
    super(name, position);
  }

  static async create(name: string, position: string) {
    const i = new ArrangeIcon(name, position);
    i.icon = await i.loadIcon();
    return i;
  }

  utility(): void {
    this.icon.on("mousedown", () => {
      iconAction.value = "arrange";
    });

    this.icon.on("mouseover", () => {
      iconAction.value = "show_arrange";
    });

    this.icon.on("mouseout", () => {
      iconAction.value = "hide_icon";
    })
  }
}

export const loadIcons = async (canvas: fabric.Canvas) => {
  lockIcon.value = await LockIcon.create("lock", "left");
  unlockIcon.value = await LockIcon.create("unlock", "left");
  arrangeIcon.value = await ArrangeIcon.create("arrange", "center");
  deleteIcon.value = await DeleteIcon.create("delete", "right");

  lockIcon.value.utility();
  unlockIcon.value.utility();
  deleteIcon.value.utility();
  arrangeIcon.value.utility();

  //It can be any of the three icons
  iconsHelperLine.value.set({
    x1: lockIcon.value.getWidth() / 2,
    y1: lockIcon.value.getHeight() / 2,
    x2: lockIcon.value.getWidth() * 3 + lockIcon.value.padding * 2,
    y2: lockIcon.value.getHeight() / 2,
  });

  canvas.add(
    unlockIcon.value.icon,
    lockIcon.value.icon,
    deleteIcon.value.icon,
    arrangeIcon.value.icon,
    iconsHelperLine.value
  );
};

/*
    FIRE ON CANVAS ZOOM, CANVAS ORIENTATION CHANGE
    WHEN CANVAS OBJECTS ARE BEING MOVED, SCALED, ROTATED
    ON CERTAIN MOUSEDOWN CONDITIONS
*/
export const hideIcons = () => {
  if (
    lockIcon.value.icon &&
    unlockIcon.value.icon &&
    deleteIcon.value.icon &&
    arrangeIcon.value.icon
  ) {
    lockIcon.value.hide();
    unlockIcon.value.hide();
    deleteIcon.value.hide();
    arrangeIcon.value.hide();
  }
};

export const showIcons = (activeObject?: fabric.Object) => {
  activeObject = activeObject || currentActiveObject.value;

  if(!activeObject) {
    return;
  }

  rotateHelperLine(activeObject, lockIcon.value.getHeight());

  deleteIcon.value.show(activeObject);
  arrangeIcon.value.show(activeObject);

  if (activeObject.lockMovementX && activeObject.lockMovementY) {
    unlockIcon.value.show(activeObject);
    lockIcon.value.hide();
  } else {
    unlockIcon.value.hide();
    lockIcon.value.show(activeObject);
  }
};

export const rotateHelperLine = (activeObject: fabric.Object, iconHeight: number) => {
  const step = 5;
  const helper = 180 / Math.PI;
  const currentAngle = Math.round(activeObject.angle! / step) * step;

  const radians = currentAngle / helper;
  const radius = iconHeight + (activeObject.height! * activeObject.scaleY!) / 2;

  const pointX = activeObject.left! + radius * -Math.sin(radians);
  const pointY = activeObject.top! - radius * -Math.cos(radians);

  iconsHelperLine.value
    .set({
      left: pointX,
      top: pointY,
      angle: currentAngle,
    })
    .setCoords();
};
